export interface NetworkLogEntry {
  id: string;
  url: string;
  method: string;
  status: number | null;
  statusText: string;
  duration: number | null;
  requestHeaders: Record<string, string>;
  responseHeaders: Record<string, string>;
  requestBody: string | null;
  responseBody: string | null;
  timestamp: string;
  error: string | null;
}

const MAX_ENTRIES = 50;
const RESPONSE_BODY_LIMIT = 5120; // 5KB
const REQUEST_BODY_LIMIT = 1024; // 1KB
const EXCLUDED_URLS = ['/api/v1/bug-reports', '/api/v1/support'];

let buffer: NetworkLogEntry[] = [];
let patched = false;

function truncate(str: string, limit: number): string {
  return str.length > limit ? str.slice(0, limit) + '...[truncated]' : str;
}

function extractHeaders(headers: HeadersInit | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  if (!headers) return result;
  const h = new Headers(headers);
  h.forEach((v, k) => {
    result[k] = k.toLowerCase() === 'authorization' ? '[REDACTED]' : v;
  });
  return result;
}

function pushEntry(entry: NetworkLogEntry): void {
  if (buffer.length >= MAX_ENTRIES) buffer.shift();
  buffer.push(entry);
}

export function initNetworkLogger(): void {
  if (patched) return;
  patched = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : (input as Request).url;

    // Don't log support/bug-report submissions to avoid recursion
    if (EXCLUDED_URLS.some(u => url.includes(u))) {
      return originalFetch(input, init);
    }

    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const startTime = performance.now();

    let requestBody: string | null = null;
    if (init?.body && typeof init.body === 'string') {
      requestBody = truncate(init.body, REQUEST_BODY_LIMIT);
    }

    const entry: NetworkLogEntry = {
      id: crypto.randomUUID(),
      url,
      method,
      status: null,
      statusText: '',
      duration: null,
      requestHeaders: extractHeaders(init?.headers),
      responseHeaders: {},
      requestBody,
      responseBody: null,
      timestamp: new Date().toISOString(),
      error: null,
    };

    try {
      const response = await originalFetch(input, init);
      entry.status = response.status;
      entry.statusText = response.statusText;
      entry.duration = Math.round(performance.now() - startTime);

      const respHeaders: Record<string, string> = {};
      response.headers.forEach((v, k) => { respHeaders[k] = v; });
      entry.responseHeaders = respHeaders;

      // Read response body for both errors and success
      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('application/json') || contentType.includes('text/')) {
        try {
          const clone = response.clone();
          const text = await clone.text();
          entry.responseBody = truncate(text, RESPONSE_BODY_LIMIT);
        } catch {
          // clone() or text() failed
        }
      }

      pushEntry(entry);
      return response;
    } catch (err) {
      entry.error = err instanceof Error ? err.message : String(err);
      entry.duration = Math.round(performance.now() - startTime);
      pushEntry(entry);
      throw err;
    }
  };
}

export function getNetworkLogs(): NetworkLogEntry[] {
  return [...buffer];
}

export function clearNetworkLogs(): void {
  buffer = [];
}
